import { useEffect, useState } from 'react';
import type { LlmModel } from '@verybigthings/trackframe-shared/schemas';
import { hub } from '@/lib/hub-client';
import {
  buildSavePlan,
  EMPTY_TRACK_SETTINGS,
  settingsFromRows,
  TrackSettingsForm,
  type TrackSettingOp,
  type TrackSettingsFormValues,
} from '../track-settings';
import { TrackApiKeyCard } from '../track-api-key';

/**
 * One setting call, reporting its own outcome — a failure on one key must not
 * hide a success on another, so the message is collected rather than thrown.
 * Returns `null` on success, or the server's message (e.g. the typed
 * `unknown_model` error) prefixed with the key it belongs to.
 */
async function applySettingOp(trackId: string, op: TrackSettingOp): Promise<string | null> {
  try {
    if (op.op === 'delete') await hub.tracksSettingsDelete({ params: { id: trackId, key: op.key } });
    else await hub.tracksSettingsPut({ params: { id: trackId, key: op.key }, body: { value: op.value } });
    return null;
  } catch (err) {
    return `${op.key}: ${(err as Error).message}`;
  }
}

/**
 * The Settings tab of the track page (HUB-109 / TAZ-07): the Track settings
 * card and the API key card, moved here from the Edit page, which keeps only
 * the manifest form.
 *
 * HUB-085 D1: the Track settings card is a SEPARATE form with its own Save.
 * It writes `tf_track_setting` rows through the typed client, not the
 * manifest-upsert path, and the two must not be coupled — a settings
 * validation error never appears to fail the track save.
 */
export function TrackSettingsPanel({ trackId }: { trackId: string }) {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [settings, setSettings] = useState<TrackSettingsFormValues>(EMPTY_TRACK_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  useEffect(() => {
    if (!trackId) return;
    Promise.all([hub.llmModelsList(), hub.tracksSettingsList({ params: { id: trackId } })])
      .then(([modelRows, settingRows]) => {
        setModels(modelRows);
        setSettings(settingsFromRows(settingRows));
      })
      .catch((err) => setSettingsError((err as Error).message))
      .finally(() => setSettingsLoading(false));
  }, [trackId]);

  const patchSettings = (patch: Partial<TrackSettingsFormValues>) =>
    setSettings((prev) => ({ ...prev, ...patch }));

  const saveSettings = async () => {
    if (!trackId) return;
    const { ops, errors } = buildSavePlan(settings);
    setSavingSettings(true);
    setSettingsError(null);
    const outcomes = await Promise.all(ops.map((op) => applySettingOp(trackId, op)));
    const failures = [...errors, ...outcomes.filter((o): o is string => o !== null)];
    setSettingsError(failures.length > 0 ? failures.join(' ') : null);
    setSavingSettings(false);
  };

  return (
    <div className="space-y-4">
      <TrackSettingsForm
        models={models}
        loading={settingsLoading}
        tokenLimit={settings.tokenLimit}
        modelId={settings.modelId}
        plannerModelId={settings.plannerModelId}
        plannerWallTimeMs={settings.plannerWallTimeMs}
        plannerReadCallLimit={settings.plannerReadCallLimit}
        onTokenLimit={(v) => patchSettings({ tokenLimit: v })}
        onModelId={(v) => patchSettings({ modelId: v })}
        onPlannerModelId={(v) => patchSettings({ plannerModelId: v })}
        onPlannerWallTimeMs={(v) => patchSettings({ plannerWallTimeMs: v })}
        onPlannerReadCallLimit={(v) => patchSettings({ plannerReadCallLimit: v })}
        onSave={() => void saveSettings()}
        saving={savingSettings}
        error={settingsError}
      />
      <TrackApiKeyCard trackId={trackId} />
    </div>
  );
}
